import React, { useCallback, useRef, useState } from 'react';
import { LexicalComposer } from '@lexical/react/LexicalComposer';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { ContentEditable } from '@lexical/react/LexicalContentEditable';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { AutoLinkNode, LinkNode } from '@lexical/link';
import { HeadingNode } from '@lexical/rich-text';
import { EditorState, LexicalEditor as LexicalEditorInstance, $getSelection, $isRangeSelection, FORMAT_TEXT_COMMAND } from 'lexical';
import { TOGGLE_LINK_COMMAND } from '@lexical/link';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { Link, Bold, Italic } from 'lucide-react';

const theme = {
  text: {
    bold: 'font-bold',
    italic: 'italic',
    underline: 'underline',
  },
  link: 'text-blue-600 hover:text-blue-800 underline cursor-pointer',
  paragraph: 'mb-2 last:mb-0',
};

const initialConfig = {
  namespace: 'SimpleLexicalEditor',
  theme,
  onError: (error: Error) => {
    console.error('Lexical error:', error);
  },
  nodes: [
    HeadingNode,
    LinkNode,
    AutoLinkNode,
  ],
};

interface SimpleLexicalEditorProps {
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

// Toolbar component
const ToolbarPlugin: React.FC<{ disabled?: boolean }> = ({ disabled }) => {
  const [editor] = useLexicalComposerContext();
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');

  const insertLink = useCallback(() => {
    if (!linkUrl.trim()) return;
    
    editor.update(() => {
      const selection = $getSelection();
      if ($isRangeSelection(selection)) {
        editor.dispatchCommand(TOGGLE_LINK_COMMAND, linkUrl);
      }
    });
    
    setShowLinkDialog(false);
    setLinkUrl('');
  }, [editor, linkUrl]);

  const handleLinkClick = useCallback(() => {
    if (disabled) return;
    setShowLinkDialog(true);
  }, [disabled]);

  const formatBold = useCallback(() => {
    if (disabled) return;
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
  }, [editor, disabled]);

  const formatItalic = useCallback(() => {
    if (disabled) return;
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
  }, [editor, disabled]);

  if (disabled) return null;

  return (
    <div className="flex items-center gap-1 p-2 border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 rounded-t-md">
      <button
        type="button"
        onClick={formatBold}
        className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300"
        title="Bold"
      >
        <Bold className="h-4 w-4" />
      </button>
      
      <button
        type="button"
        onClick={formatItalic}
        className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300"
        title="Italic"
      >
        <Italic className="h-4 w-4" />
      </button>
      
      <div className="w-px h-6 bg-gray-300 dark:bg-gray-500 mx-1" />
      
      <button
        type="button"
        onClick={handleLinkClick}
        className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-600 dark:text-gray-300"
        title="Add Link"
      >
        <Link className="h-4 w-4" />
      </button>

      {showLinkDialog && (
        <div className="absolute top-full left-0 right-0 z-10 mt-1 p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg">
          <div className="flex items-center gap-2">
            <input
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="Enter URL (e.g., https://example.com)"
              className="flex-1 px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  insertLink();
                } else if (e.key === 'Escape') {
                  setShowLinkDialog(false);
                  setLinkUrl('');
                }
              }}
            />
            <button
              type="button"
              onClick={insertLink}
              className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
            >
              Add
            </button>
            <button
              type="button"
              onClick={() => {
                setShowLinkDialog(false);
                setLinkUrl('');
              }}
              className="px-3 py-1 text-sm bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-400 dark:hover:bg-gray-500"
            >
              Cancel
            </button>
          </div>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Select text first, then add a link to make it clickable
          </p>
        </div>
      )}
    </div>
  );
};

const SimpleLexicalEditor: React.FC<SimpleLexicalEditorProps> = ({
  value = '',
  onChange,
  placeholder = 'Enter description...',
  disabled = false,
  className = '',
}) => {
  const editorRef = useRef<LexicalEditorInstance | null>(null);

  const handleChange = useCallback((editorState: EditorState, editor: LexicalEditorInstance) => {
    editorRef.current = editor;
    if (onChange) {
      const serializedState = JSON.stringify(editorState.toJSON());
      onChange(serializedState);
    }
  }, [onChange]);

  // Create initial editor state from value
  const getInitialEditorState = useCallback(() => {
    if (!value || value.trim() === '') {
      return undefined; // Let Lexical create empty state
    }
    
    try {
      // Try to parse as JSON (Lexical state)
      JSON.parse(value);
      return value;
    } catch {
      // If not JSON, treat as plain text and create a simple paragraph
      return JSON.stringify({
        root: {
          children: [
            {
              children: [
                {
                  detail: 0,
                  format: 0,
                  mode: 'normal',
                  style: '',
                  text: value,
                  type: 'text',
                  version: 1,
                },
              ],
              direction: null,
              format: '',
              indent: 0,
              type: 'paragraph',
              version: 1,
            },
          ],
          direction: null,
          format: '',
          indent: 0,
          type: 'root',
          version: 1,
        },
      });
    }
  }, [value]);

  const ErrorBoundary = ({ children }: { children: React.ReactNode }) => {
    return <div>{children}</div>;
  };

  return (
    <LexicalComposer 
      initialConfig={{
        ...initialConfig,
        editorState: getInitialEditorState(),
        editable: !disabled,
      }}
    >
      <div className={`relative border border-gray-300 dark:border-gray-600 rounded-md ${className}`}>
        <ToolbarPlugin disabled={disabled} />
        <RichTextPlugin
          contentEditable={
            <ContentEditable
              className={`
                min-h-[80px] p-3 bg-white dark:bg-gray-800 text-gray-900 dark:text-white
                focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
                disabled:bg-gray-100 dark:disabled:bg-gray-700 disabled:text-gray-500
                resize-none overflow-auto rounded-b-md
                ${disabled ? 'cursor-not-allowed opacity-60' : ''}
              `}
              style={{ 
                userSelect: disabled ? 'none' : 'auto',
                pointerEvents: disabled ? 'none' : 'auto'
              }}
            />
          }
          placeholder={
            <div className="absolute top-12 left-3 text-gray-400 dark:text-gray-500 pointer-events-none select-none">
              {placeholder}
            </div>
          }
          ErrorBoundary={ErrorBoundary}
        />
        <OnChangePlugin onChange={handleChange} />
        <HistoryPlugin />
        <LinkPlugin />
      </div>
    </LexicalComposer>
  );
};

export default SimpleLexicalEditor;
